import { useEffect, useState } from 'react';
import { CompanyFormInput } from '../../Types';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormTextField,
  FormCheckboxGroup,
} from '../../Components/Form';

const CompanyForm: React.FC<{
  onSubmit: (data: CompanyFormInput) => void;
  onCancel: () => void;
  defaultValues?: CompanyFormInput;
}> = ({ onSubmit, onCancel, defaultValues }) => {
  const [formData, setFormData] = useState<CompanyFormInput>({
    name: '',
    P21CustomerID: '',
    IsActive: true,
    IsAutoInvoice: false,
    IsConsolidatedInvoice: false,
    IsP21Company: false,
    IsDemoAccount: false,
    IsTrustedCustomer: false,
    PriceVisibleFlag: true,
    SpendingLimitEnableFlag: false,
    ParentCompanyID: undefined,
    OrganizationID: '',
    BillingDay: undefined,
    BillingTime: undefined,
  });

  useEffect(() => {
    if (defaultValues) {
      setFormData({
        name: defaultValues.name || '',
        P21CustomerID: defaultValues.P21CustomerID || '',
        IsActive: !!defaultValues.IsActive,
        IsAutoInvoice: !!defaultValues.IsAutoInvoice,
        IsConsolidatedInvoice: !!defaultValues.IsConsolidatedInvoice,
        IsP21Company: !!defaultValues.IsP21Company,
        IsDemoAccount: !!defaultValues.IsDemoAccount,
        IsTrustedCustomer: !!defaultValues.IsTrustedCustomer,
        PriceVisibleFlag: !!defaultValues.PriceVisibleFlag,
        SpendingLimitEnableFlag: !!defaultValues.SpendingLimitEnableFlag,
        ParentCompanyID: defaultValues.ParentCompanyID ?? undefined,
        OrganizationID: defaultValues.OrganizationID || '',
        BillingDay: defaultValues.BillingDay ?? undefined,
        BillingTime: defaultValues.BillingTime ?? undefined,
      });
    }
  }, [defaultValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'ParentCompanyID' || name === 'BillingDay' || name === 'BillingTime'
            ? Number(value)
            : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const checkboxOptions = [
    { name: 'IsActive', label: 'Is Active', checked: formData.IsActive },
    { name: 'IsAutoInvoice', label: 'Auto Invoice', checked: !!formData.IsAutoInvoice },
    { name: 'IsConsolidatedInvoice', label: 'Consolidated Invoice', checked: !!formData.IsConsolidatedInvoice },
    { name: 'IsP21Company', label: 'P21 Company', checked: !!formData.IsP21Company },
    { name: 'IsDemoAccount', label: 'Demo Account', checked: !!formData.IsDemoAccount },
    { name: 'IsTrustedCustomer', label: 'Trusted Customer', checked: !!formData.IsTrustedCustomer },
    { name: 'PriceVisibleFlag', label: 'Price Visible', checked: !!formData.PriceVisibleFlag },
    { name: 'SpendingLimitEnableFlag', label: 'Spending Limit', checked: !!formData.SpendingLimitEnableFlag },
  ];

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      title={defaultValues ? 'Edit Company' : 'Add Company'}
      subtitle="Configure company settings, billing, and feature flags."
      submitLabel="Save Company"
      onCancel={onCancel}
    >
      <FormSection>
        <SectionTitle>General Information</SectionTitle>
        <SectionDescription>Basic company identification and billing schedule.</SectionDescription>
        <FormGrid>
          <FormTextField
            label="Company Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <FormTextField
            label="Parent Company ID"
            name="ParentCompanyID"
            type="number"
            value={formData.ParentCompanyID || ''}
            onChange={handleChange}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e') e.preventDefault();
            }}
          />
          <FormTextField
            label="Billing Day (1–31)"
            name="BillingDay"
            type="number"
            value={formData.BillingDay || ''}
            disabled={!formData.IsAutoInvoice}
            onChange={handleChange}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e') e.preventDefault();
            }}
          />
          <FormTextField
            label="Billing Time"
            name="BillingTime"
            type="number"
            value={formData.BillingTime || ''}
            onChange={handleChange}
            disabled={!formData.IsAutoInvoice}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e') e.preventDefault();
            }}
          />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Settings</SectionTitle>
        <SectionDescription>Toggle company features and account options.</SectionDescription>
        <FormCheckboxGroup options={checkboxOptions} onChange={handleChange} columns={2} />
      </FormSection>
    </Form>
  );
};

export default CompanyForm;
